import { useCallback, useEffect, useRef, useState } from 'react';
import {
  productsService,
  type ProductView,
  type ProductVariantsCombosView,
} from '../../../../services/products/products.service';
import type { OrderlineView } from '../../../../services/orderlines/orderlines.service';
import type { VariantComboGroupView } from '../../../../services/variants-combos/variants-combos.service';
import type { OrderlineSelectionInput, Selected } from '../../components/orders/variant-combo-selection-modal/VariantComboSelectionModal';
import type { ComboSelectionRoute } from '../../../../utils/orderline-selections';

export type SelectionModalState = {
  orderId: number;
  product: ProductView;
  groups: VariantComboGroupView[];
  editingLine?: OrderlineView;
  initialSelections?: Selected;
} | null;

type Params = {
  tenantId: string;
  employeeSession: string | null;
  posProducts: ProductView[];
  orderlines: OrderlineView[];
  addOrderline: (orderId: number, productId: number) => void;
  addConfiguredOrderline: (orderId: number, productId: number, selections: OrderlineSelectionInput[]) => void | Promise<void>;
  updateOrderlineSelections: (line: OrderlineView, selections: OrderlineSelectionInput[]) => void | Promise<void>;
};

// Architectural summary: Owns the POS variant/combo selection modal (configured add + edit-in-place) so
export function useVariantComboSelection({
  tenantId,
  employeeSession,
  posProducts,
  orderlines,
  addOrderline,
  addConfiguredOrderline,
  updateOrderlineSelections,
}: Params) {
  const [selectionModal, setSelectionModal] = useState<SelectionModalState>(null);
  const probeInFlightRef = useRef(false);
  const confirmInFlightRef = useRef(false);
  const compositionCacheRef = useRef<Map<number, ProductVariantsCombosView>>(new Map());
  const posProductsRef = useRef(posProducts);
  posProductsRef.current = posProducts;

  const fetchComposition = useCallback(
    async (productId: number): Promise<ProductVariantsCombosView | null> => {
      if (!employeeSession) {
        return null;
      }
      const cached = compositionCacheRef.current.get(productId);
      if (cached) {
        return cached;
      }
      const composition = await productsService.getComposition(tenantId, productId, employeeSession);
      compositionCacheRef.current.set(productId, composition);
      return composition;
    },
    [employeeSession, tenantId],
  );

  const invalidateComposition = useCallback((productId: number) => {
    compositionCacheRef.current.delete(productId);
    // If the selection modal is open for this exact product (a waiter mid-selection while a
    // manager just edited that product's variants/combos), refresh it live instead of leaving it
    // showing a stale snapshot until closed and reopened. Read/write via the setter's functional
    // form (not a ref) so this always sees the actual current state, even when invalidateComposition
    // and a close/open call land in the same render batch.
    setSelectionModal((current) => {
      if (current && current.product.id === productId && !current.editingLine) {
        void (async () => {
          const fresh = await fetchComposition(productId);
          if (!fresh) {
            return;
          }
          setSelectionModal((latest) =>
            latest && latest.product.id === productId && !latest.editingLine
              ? { ...latest, groups: fresh.groups }
              : latest,
          );
        })();
      }
      return current;
    });
  }, [fetchComposition]);

  const resolveComboSelectionRoute = useCallback((productId: number, itemId: number): ComboSelectionRoute | null => {
    const composition = compositionCacheRef.current.get(productId);
    if (!composition) {
      return null;
    }
    const item = findVariantComboItem(composition.groups, itemId);
    return item ? { kds: item.kds, printerIds: item.printerIds ?? [] } : null;
  }, []);

  const handleProductTap = useCallback(
    (orderId: number, productId: number) => {
      if (!employeeSession || probeInFlightRef.current) {
        return;
      }
      probeInFlightRef.current = true;
      const product = posProductsRef.current.find((p) => p.id === productId);
      void (async () => {
        try {
          const composition = await fetchComposition(productId);
          if (composition?.hasVariantsCombos && product) {
            setSelectionModal({ orderId, product, groups: composition.groups });
            return;
          }
        } catch {
        } finally {
          probeInFlightRef.current = false;
        }
        addOrderline(orderId, productId);
      })();
    },
    [addOrderline, employeeSession, fetchComposition],
  );

  const handleEditSelections = useCallback(
    (line: OrderlineView) => {
      if (!employeeSession || line.status !== 'pending' || line.productId == null || probeInFlightRef.current) {
        return;
      }
      probeInFlightRef.current = true;
      const productId = line.productId;
      const product = posProductsRef.current.find((p) => p.id === productId);
      void (async () => {
        try {
          const composition = await fetchComposition(productId);
          if (!composition?.hasVariantsCombos || !product) {
            return;
          }
          const initialSelections: Selected = {};
          for (const sel of line.selections ?? []) {
            if (sel.itemId != null) {
              initialSelections[sel.itemId] = sel.quantity;
            }
          }
          setSelectionModal({ orderId: line.orderId, product, groups: composition.groups, editingLine: line, initialSelections });
        } catch {
        } finally {
          probeInFlightRef.current = false;
        }
      })();
    },
    [employeeSession, fetchComposition],
  );

  const handleConfirmSelections = useCallback(
    (selections: OrderlineSelectionInput[]) => {
      if (!selectionModal || confirmInFlightRef.current) {
        return;
      }
      const { orderId, product, editingLine } = selectionModal;
      if (editingLine && hasSameSelectionQuantities(editingLine, selections)) {
        setSelectionModal(null);
        return;
      }
      confirmInFlightRef.current = true;
      setSelectionModal(null);
      const mutation = editingLine
        ? updateOrderlineSelections(editingLine, selections)
        : addConfiguredOrderline(orderId, product.id, selections);
      void Promise.resolve(mutation).finally(() => {
        confirmInFlightRef.current = false;
      });
    },
    [selectionModal, addConfiguredOrderline, updateOrderlineSelections],
  );

  const closeSelectionModal = useCallback(() => setSelectionModal(null), []);

  const editingLineId = selectionModal?.editingLine?.id;
  useEffect(() => {
    if (editingLineId !== undefined && !orderlines.some((l) => l.id === editingLineId && l.status === 'pending')) {
      setSelectionModal(null);
    }
  }, [orderlines, editingLineId]);

  return {
    selectionModal,
    handleProductTap,
    handleEditSelections,
    handleConfirmSelections,
    closeSelectionModal,
    invalidateComposition,
    resolveComboSelectionRoute,
  };
}

function findVariantComboItem(groups: VariantComboGroupView[], itemId: number): VariantComboGroupView['items'][number] | null {
  for (const group of groups) {
    for (const item of group.items) {
      if (item.id === itemId) {
        return item;
      }
      const nested = findVariantComboItem(item.childGroups, itemId);
      if (nested) {
        return nested;
      }
    }
  }
  return null;
}

function hasSameSelectionQuantities(line: OrderlineView, selections: OrderlineSelectionInput[]): boolean {
  const current = new Map<number, number>();
  for (const sel of line.selections ?? []) {
    if (sel.itemId == null) {
      continue;
    }
    const nextQty = (current.get(sel.itemId) ?? 0) + (sel.quantity ?? 0);
    current.set(sel.itemId, nextQty);
  }

  const incoming = new Map<number, number>();
  for (const sel of selections) {
    const nextQty = (incoming.get(sel.itemId) ?? 0) + sel.selectedQuantity;
    incoming.set(sel.itemId, nextQty);
  }

  if (current.size !== incoming.size) {
    return false;
  }

  for (const [itemId, qty] of current.entries()) {
    if ((incoming.get(itemId) ?? 0) !== qty) {
      return false;
    }
  }

  return true;
}
